import React, { useState } from 'react';
import './PostAd.css';
import { collection, addDoc, query, where, getCountFromServer } from "firebase/firestore";
import { db } from "../firebase/config";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { uploadToCloudinary } from '../utils/cloudinary';
import SuccessModal from '../components/SuccessModal';

const PostAd = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Step State: 1 = Select Category, 2 = Fill Form
    const [step, setStep] = useState(1);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [adCount, setAdCount] = useState(0);
    const [isCheckingCount, setIsCheckingCount] = useState(true);

    const slugify = (text) => {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]+/g, '')
            .replace(/--+/g, '-');
    };

    React.useEffect(() => {
        if (!user) {
            setIsCheckingCount(false);
            return;
        }

        const checkAdCount = async () => {
            try {
                const q = query(collection(db, "ads"), where("userId", "==", user.uid));
                const snapshot = await getCountFromServer(q);
                setAdCount(snapshot.data().count);
            } catch (err) {
                console.error("Error checking ad count:", err);
            } finally {
                setIsCheckingCount(false);
            }
        };

        checkAdCount();
    }, [user]);

    const isLimitReached = user?.userType === 'Individual' && adCount >= 3;

    // Initial State
    const [formData, setFormData] = useState({
        title: '',
        vehicleCondition: 'Used',
        vehicleType: 'Car',
        vehicleMake: '',
        model: '',
        manufacturedYear: '',
        price: '',
        transmission: 'Automatic',
        fuelType: 'Petrol',
        engineCapacity: '',
        mileage: '',
        location: '',
        district: '',
        city: '',
        description: '',
        partCategory: '',
        depositAmount: '',
        rentalTerm: '',
        servicedVehicleTypes: [], // New field for Service Station tags
        useAccountPhone: true,
        customPhone: '',
        options: {
            airCondition: false,
            powerSteering: false,
            powerMirror: false,
            powerWindow: false
        }
    });

    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    // Field Options
    const vehicleConditions = ["New", "Used", "Reconditioned"];
    const vehicleTypes = ["Car", "Van", "SUV", "Jeep", "Motorbike", "Three Wheeler", "Truck", "Bus", "Lorry", "Bicycle", "Parts & Accessories", "Service Station"];
    const transmissions = ["Automatic", "Manual", "Tiptronic"];
    const fuelTypes = ["Petrol", "Diesel", "Hybrid", "Electric", "None"]; // None for Bicycles/Parts
    const years = Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i);

    const vehicleMakesData = {
        Car: ["Toyota", "Honda", "Nissan", "Suzuki", "Mitsubishi", "Mazda", "Kia", "Hyundai", "BMW", "Mercedes-Benz", "Audi", "Peugeot", "Micro", "Renault", "MG", "Tata", "Other"],
        Van: ["Toyota", "Nissan", "Micro", "Suzuki", "Mitsubishi", "Mazda", "Other"],
        SUV: ["Toyota", "Mitsubishi", "Nissan", "Honda", "Land Rover", "BMW", "Mercedes-Benz", "Audi", "Kia", "Hyundai", "MG", "Porsche", "Other"],
        Jeep: ["Toyota", "Mitsubishi", "Land Rover", "Nissan", "Jeep", "Other"],
        Motorbike: ["Bajaj", "TVS", "Hero", "Honda", "Yamaha", "Suzuki", "KTM", "Royal Enfield", "Kawasaki", "Demak", "Loncin", "Other"],
        "Three Wheeler": ["Bajaj", "TVS", "Piaggio", "Mahindra", "Other"],
        Truck: ["Isuzu", "Mitsubishi", "Toyota", "Nissan", "Tata", "Ashok Leyland", "Mahindra", "Dimo", "Other"],
        Bus: ["Ashok Leyland", "Tata", "Toyota", "Nissan", "Isuzu", "Mitsubishi", "Micro", "Other"],
        Lorry: ["Tata", "Ashok Leyland", "Isuzu", "Mitsubishi", "Mahindra", "Nissan", "Toyota", "Dimo", "Other"],
        Bicycle: ["Lumala", "Hero", "Kent", "Tomahawk", "Raleigh", "Other"],
    };

    const currentMakes = vehicleMakesData[formData.vehicleType] || [];

    const sriLankaLocations = {
        "Ampara": ["Ampara", "Akkaraipattu", "Kalmunai", "Samanthurai", "Pottuvil", "Dehiattakandiya"],
        "Anuradhapura": ["Anuradhapura", "Kekirawa", "Tambuttegama", "Medawachchiya", "Eppawala", "Nochchiyagama", "Mihintale", "Galenbindunuwewa"],
        "Badulla": ["Badulla", "Bandarawela", "Haputale", "Welimada", "Mahiyanganaya", "Passara", "Diyatalawa", "Hali Ela"],
        "Batticaloa": ["Batticaloa", "Valaichchenai", "Kalkudah", "Kattankudy", "Eravur", "Chenkalady"],
        "Colombo": ["Colombo 1", "Colombo 2", "Colombo 3", "Colombo 4", "Colombo 5", "Colombo 6", "Colombo 7", "Colombo 8", "Colombo 9", "Colombo 10", "Colombo 11", "Colombo 12", "Colombo 13", "Colombo 14", "Colombo 15", "Dehiwala", "Mount Lavinia", "Moratuwa", "Peliyagoda", "Nugegoda", "Maharagama", "Piliyandala", "Homagama", "Awissawella", "Kaduwela", "Battaramulla", "Malabe", "Kolonnawa", "Wellampitiya", "Boralesgamuwa", "Rajagiriya", "Nawala", "Kohuwala", "Ratmalana", "Angoda", "Mulleriyawa", "Padukka", "Hanwella", "Meegoda"],
        "Galle": ["Galle", "Ambalangoda", "Elpitiya", "Hikkaduwa", "Baddegama", "Koggala", "Ahangama", "Batapola", "Karapitiya"],
        "Gampaha": ["Gampaha", "Negombo", "Kelaniya", "Kiribathgoda", "Kadawatha", "Ragama", "Wattala", "Ja-Ela", "Kandana", "Minuwangoda", "Nittambuwa", "Veyangoda", "Mirigama", "Dompe", "Biyagama", "Peliyagoda", "Ganemulla", "Katunayake"],
        "Hambantota": ["Hambantota", "Tangalle", "Beliatta", "Ambalantota", "Tissamaharama", "Weeraketiya", "Walasmulla", "Middeniya"],
        "Jaffna": ["Jaffna", "Chavakachcheri", "Point Pedro", "Valvettithurai", "Nallur", "Kankesanthurai", "Karainagar", "Chunnakam"],
        "Kalutara": ["Kalutara", "Panadura", "Horana", "Bandaragama", "Matugama", "Aluthgama", "Beruwala", "Wadduwa", "Ingiriya", "Agalawatta", "Dodangoda", "Payagala"],
        "Kandy": ["Kandy", "Peradeniya", "Katugastota", "Gampola", "Nawalapitiya", "Kadugannawa", "Pilimathalawa", "Digana", "Wattegama", "Akurana", "Galagedara", "Madawala"],
        "Kegalle": ["Kegalle", "Mawanella", "Warakapola", "Ruwanwella", "Rambukkana", "Galigamuwa", "Deraniyagala", "Yatiyantota", "Kitulgala"],
        "Kilinochchi": ["Kilinochchi", "Pallai", "Paranthan", "Pooneryn"],
        "Kurunegala": ["Kurunegala", "Kuliyapitiya", "Polgahawela", "Pannala", "Mawathagama", "Wariyapola", "Narammala", "Giriulla", "Alawwa", "Ibbagamuwa", "Melsiripura"],
        "Mannar": ["Mannar", "Murunkan", "Pesalai", "Talaimannar"],
        "Matale": ["Matale", "Dambulla", "Galewela", "Ukuwela", "Sigiriya", "Palapathwela", "Yatawatta"],
        "Matara": ["Matara", "Weligama", "Akuressa", "Dickwella", "Deniyaya", "Kamburugamuwa", "Hakmana", "Devinuwara"],
        "Moneragala": ["Moneragala", "Wellawaya", "Bibile", "Kataragama", "Buttala"],
        "Mullaitivu": ["Mullaitivu", "Oddusuddan", "Puthukkudiyiruppu"],
        "Nuwara Eliya": ["Nuwara Eliya", "Hatton", "Talawakele", "Nanu Oya", "Ginigathhena", "Haputale", "Dickoya"],
        "Polonnaruwa": ["Polonnaruwa", "Kaduruwela", "Hingurakgoda", "Medirigiriya"],
        "Puttalam": ["Puttalam", "Chilaw", "Wennappuwa", "Nattandiya", "Marawila", "Dankotuwa", "Kalpitiya"],
        "Ratnapura": ["Ratnapura", "Pelmadulla", "Balangoda", "Embilipitiya", "Rakwana", "Eheliyagoda", "Kuruwita", "Nivitigala"],
        "Trincomalee": ["Trincomalee", "Muttur", "Kinniya", "Kantale"],
        "Vavuniya": ["Vavuniya", "Nedunkeni", "Cheddikulam"]
    };

    const currentCities = sriLankaLocations[formData.district] || [];

    const categories = [
        { id: 'Sell', label: 'Sell' },
        { id: 'Rent', label: 'Rent' },
        { id: 'Parts', label: 'Parts' },
        { id: 'Service Station', label: 'Service Station' }
    ];

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);

        // Pre-fill logic for the new separate categories
        if (category === 'Parts') {
            setFormData(prev => ({
                ...prev,
                vehicleType: 'Parts & Accessories',
                fuelType: 'None',
                transmission: 'Manual'
            }));
        } else if (category === 'Service Station') {
            setFormData(prev => ({
                ...prev,
                vehicleType: 'Service Station',
                fuelType: 'None',
                transmission: 'Manual'
            }));
        } else {
            // Default for Sell/Rent
            setFormData(prev => ({ ...prev, vehicleType: 'Car' }));
        }

        setStep(2);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'vehicleType') {
            setFormData(prev => ({
                ...prev,
                vehicleType: value,
                vehicleMake: '' // Reset make when type changes
            }));
        } else if (name === 'district') {
            setFormData(prev => ({
                ...prev,
                district: value,
                city: '' // Reset city when district changes
            }));
        } else if (name in formData.options) {
            setFormData(prev => ({
                ...prev,
                options: { ...prev.options, [name]: checked }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            if (filesArray.length + images.length > 6) {
                alert("You can only upload up to 6 images.");
                return;
            }
            setImages(prev => [...prev, ...filesArray]);
        }
    };

    const handleServicedTypeToggle = (type) => {
        setFormData(prev => {
            const current = prev.servicedVehicleTypes || [];
            if (current.includes(type)) {
                return { ...prev, servicedVehicleTypes: current.filter(t => t !== type) };
            } else {
                return { ...prev, servicedVehicleTypes: [...current, type] };
            }
        });
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleDragStart = (e, index) => {
        e.dataTransfer.setData("imageIndex", index);
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = e.dataTransfer.getData("imageIndex");
        if (sourceIndex === "" || Number(sourceIndex) === targetIndex) return;

        setImages(prev => {
            const arr = [...prev];
            const [draggedItem] = arr.splice(Number(sourceIndex), 1);
            arr.splice(targetIndex, 0, draggedItem);
            return arr;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Determine the final title (auto-generate for standard vehicles if empty)
            let finalTitle = formData.title;
            if (!finalTitle && (selectedCategory === 'Sell' || !selectedCategory)) {
                finalTitle = `${formData.vehicleMake} ${formData.model}`.trim();
            }
            if (!finalTitle) finalTitle = 'Vehicle Ad';

            const imageUrls = [];
            for (let i = 0; i < images.length; i++) {
                const url = await uploadToCloudinary(images[i], finalTitle, i);
                if (url) imageUrls.push(url);
            }

            // Fallback image if none uploaded
            const mainImage = imageUrls.length > 0
                ? imageUrls[0]
                : 'https://placehold.co/600x400/1e293b/ffffff?text=' + encodeURIComponent(finalTitle);

            // Check total system ads for high-traffic auto-approval
            const allAdsSnapshot = await getCountFromServer(collection(db, "ads"));
            const totalSystemAds = allAdsSnapshot.data().count;

            const finalLocation = formData.district && formData.city ? `${formData.city}, ${formData.district}` : '';

            const adData = {
                ...formData,
                location: finalLocation,
                title: finalTitle, // Ensure title is stored in DB
                category: selectedCategory,
                price: formData.price,
                image: mainImage,
                images: imageUrls,
                createdAt: new Date(),
                userId: user ? user.uid : 'anonymous',
                userEmail: user ? user.email : '',
                userName: user ? (user.fullName || 'Anonymous') : 'Anonymous',
                userPhone: formData.useAccountPhone ? (user?.phone || 'N/A') : formData.customPhone,
                views: 0,
                status: (user?.role === 'admin' || totalSystemAds >= 50) ? 'approved' : 'pending'
            };

            // Add slugs for Corporate vendors and Admins
            if (user?.userType === 'Corporate' || user?.role === 'admin') {
                adData.vendorSlug = user.slug || (user.role === 'admin' ? 'riyamaga' : '');
                adData.adSlug = slugify(finalTitle);
            }

            await addDoc(collection(db, "ads"), adData);
            setShowSuccessModal(true);
        } catch (err) {
            console.error("Error posting ad: ", err);
            setError('Failed to post ad. Please try again. ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="post-ad-container">
            <h2 className="section-title">
                {step === 1 ? 'What do you want to advertise?' : `Post Ad - ${selectedCategory}`}
            </h2>

            {isCheckingCount ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <div className="loading-spinner"></div>
                    <p style={{ marginTop: '1rem', color: 'var(--color-text-secondary)' }}>Checking ad limits...</p>
                </div>
            ) : isLimitReached ? (
                <div className="limit-notice-card">
                    <div className="limit-icon">
                        <i className="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <h3>Ad Limit Reached</h3>
                    <p>
                        Individual accounts are limited to 3 active ads. You currently have <strong>{adCount}</strong> ads.
                    </p>
                    <div className="limit-benefits">
                        <h4>Why upgrade to Corporate?</h4>
                        <ul>
                            <li><i className="fa-solid fa-check"></i> Unlimited ad listings</li>
                            <li><i className="fa-solid fa-check"></i> Custom profile URL (/your-name)</li>
                            <li><i className="fa-solid fa-check"></i> Business branding with logo & cover</li>
                            <li><i className="fa-solid fa-check"></i> Priority visibility in search</li>
                        </ul>
                    </div>
                    <div className="limit-actions">
                        <Link to="/profile" className="btn-upgrade-now">
                            <i className="fa-solid fa-arrow-up-right-from-square"></i> Upgrade to Corporate Now
                        </Link>
                        <button onClick={() => navigate('/profile')} className="btn-manage-ads">
                            Manage My Ads
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {step === 1 && (
                        <div className="category-selection">
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    className="btn-category"
                                    onClick={() => handleCategorySelect(cat.id)}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {step === 2 && (
                        <form onSubmit={handleSubmit} className="post-ad-form">
                            <button type="button" className="btn-back" onClick={() => setStep(1)}>← Back to Categories</button>
                            {error && <div className="error-message">{error}</div>}

                            {selectedCategory === 'Parts' ? (
                                <>
                                    {/* Parts Specific Form Fields */}
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Part Condition</label>
                                            <select name="vehicleCondition" value={formData.vehicleCondition} onChange={handleChange} required={user?.role !== 'admin'}>
                                                <option value="New">New</option>
                                                <option value="Used">Used</option>
                                                <option value="Reconditioned">Reconditioned</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Part Used In (Vehicle Type)</label>
                                            <select name="vehicleType" value={formData.vehicleType} onChange={handleChange} required={user?.role !== 'admin'}>
                                                <option value="Car">Car</option>
                                                <option value="Van">Van</option>
                                                <option value="SUV">SUV</option>
                                                <option value="Jeep">Jeep</option>
                                                <option value="Motorbike">Motorbike</option>
                                                <option value="Three Wheeler">Three Wheeler</option>
                                                <option value="Truck">Truck</option>
                                                <option value="Bus">Bus</option>
                                                <option value="Lorry">Lorry</option>
                                                <option value="Bicycle">Bicycle</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Part Category</label>
                                            <input type="text" name="partCategory" value={formData.partCategory} onChange={handleChange} placeholder="e.g. Engine, Body, Suspension" required={user?.role !== 'admin'} />
                                        </div>
                                        <div className="form-group">
                                            <label>Part Name and Brand</label>
                                            <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Toyota Aqua Headlight" required={user?.role !== 'admin'} />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Price (Rs.)</label>
                                        <input type="text" name="price" value={formData.price} onChange={handleChange} placeholder="e.g. 50,000" required={user?.role !== 'admin'} />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>District</label>
                                            <select name="district" value={formData.district} onChange={handleChange} required={user?.role !== 'admin'}>
                                                <option value="">Select District</option>
                                                {Object.keys(sriLankaLocations).sort().map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>City</label>
                                            <select name="city" value={formData.city} onChange={handleChange} required={user?.role !== 'admin'} disabled={!formData.district}>
                                                <option value="">Select City</option>
                                                {currentCities.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Additional Info (Description)</label>
                                        <textarea name="description" value={formData.description} onChange={handleChange} rows="4"></textarea>
                                    </div>
                                </>
                            ) : selectedCategory === 'Rent' ? (
                                <>
                                    {/* Rent Specific Form Fields */}
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Vehicle Type</label>
                                            <select name="vehicleType" value={formData.vehicleType} onChange={handleChange} required={user?.role !== 'admin'}>
                                                {vehicleTypes.filter(t => t !== "Parts & Accessories" && t !== "Service Station").map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Vehicle Brand (Make)</label>
                                            {currentMakes.length > 0 ? (
                                                <select name="vehicleMake" value={formData.vehicleMake} onChange={handleChange} required={user?.role !== 'admin'}>
                                                    <option value="">Select Make</option>
                                                    {currentMakes.map(m => <option key={m} value={m}>{m}</option>)}
                                                </select>
                                            ) : (
                                                <input type="text" name="vehicleMake" value={formData.vehicleMake} onChange={handleChange} placeholder="e.g. Toyota" required={user?.role !== 'admin'} />
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Vehicle Name (Model)</label>
                                            <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Toyota Axio for Rent" required={user?.role !== 'admin'} />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Condition</label>
                                            <select name="vehicleCondition" value={formData.vehicleCondition} onChange={handleChange} required={user?.role !== 'admin'}>
                                                {vehicleConditions.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Rental Price (Rs.)</label>
                                            <input type="text" name="price" value={formData.price} onChange={handleChange} placeholder="e.g. 5,000" required={user?.role !== 'admin'} />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Deposit Amount (Rs.)</label>
                                            <input type="text" name="depositAmount" value={formData.depositAmount} onChange={handleChange} placeholder="e.g. 50,000" required={user?.role !== 'admin'} />
                                        </div>
                                        <div className="form-group">
                                            <label>Rental Term</label>
                                            <input type="text" name="rentalTerm" value={formData.rentalTerm} onChange={handleChange} placeholder="e.g. Per Day / Per Month" required={user?.role !== 'admin'} />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>District</label>
                                            <select name="district" value={formData.district} onChange={handleChange} required={user?.role !== 'admin'}>
                                                <option value="">Select District</option>
                                                {Object.keys(sriLankaLocations).sort().map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>City</label>
                                            <select name="city" value={formData.city} onChange={handleChange} required={user?.role !== 'admin'} disabled={!formData.district}>
                                                <option value="">Select City</option>
                                                {currentCities.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Description (Additional Info)</label>
                                        <textarea name="description" value={formData.description} onChange={handleChange} rows="4"></textarea>
                                    </div>
                                </>
                            ) : selectedCategory === 'Service Station' ? (
                                <>
                                    {/* Service Station Specific Form Fields */}
                                    <div className="form-group">
                                        <label>Service Station Name</label>
                                        <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Super Shine Auto Care" required={user?.role !== 'admin'} />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>District</label>
                                            <select name="district" value={formData.district} onChange={handleChange} required={user?.role !== 'admin'}>
                                                <option value="">Select District</option>
                                                {Object.keys(sriLankaLocations).sort().map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>City</label>
                                            <select name="city" value={formData.city} onChange={handleChange} required={user?.role !== 'admin'} disabled={!formData.district}>
                                                <option value="">Select City</option>
                                                {currentCities.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Vehicle Types Serviced (Click to add)</label>
                                        <div className="tags-container">
                                            {["Car", "Van", "SUV", "Jeep", "Motorbike", "Three Wheeler", "Truck", "Bus", "Lorry", "Bicycle"].map(type => (
                                                <button
                                                    key={type}
                                                    type="button"
                                                    className={`tag-btn ${formData.servicedVehicleTypes?.includes(type) ? 'active' : ''}`}
                                                    onClick={() => handleServicedTypeToggle(type)}
                                                >
                                                    {type}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Services Offered & Additional Info</label>
                                        <textarea name="description" value={formData.description} onChange={handleChange} rows="4" placeholder="e.g. Full service, Engine tuning, Nano coating..."></textarea>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Standard Vehicle Form Fields */}
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Vehicle Type</label>
                                            <select name="vehicleType" value={formData.vehicleType} onChange={handleChange} required={user?.role !== 'admin'}>
                                                {vehicleTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Condition</label>
                                            <select name="vehicleCondition" value={formData.vehicleCondition} onChange={handleChange} required={user?.role !== 'admin'}>
                                                {vehicleConditions.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Make</label>
                                            {currentMakes.length > 0 ? (
                                                <select name="vehicleMake" value={formData.vehicleMake} onChange={handleChange} required={user?.role !== 'admin'}>
                                                    <option value="">Select Make</option>
                                                    {currentMakes.map(m => <option key={m} value={m}>{m}</option>)}
                                                </select>
                                            ) : (
                                                <input type="text" name="vehicleMake" value={formData.vehicleMake} onChange={handleChange} placeholder="e.g. Toyota" required={user?.role !== 'admin'} />
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <label>Model</label>
                                            <input type="text" name="model" value={formData.model} onChange={handleChange} placeholder="e.g. Aqua" required={user?.role !== 'admin'} />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Manufactured Year</label>
                                            <select name="manufacturedYear" value={formData.manufacturedYear} onChange={handleChange} required={user?.role !== 'admin'}>
                                                <option value="">Select Year</option>
                                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Transmission</label>
                                            <select name="transmission" value={formData.transmission} onChange={handleChange} required={user?.role !== 'admin'}>
                                                {transmissions.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Fuel Type</label>
                                            <select name="fuelType" value={formData.fuelType} onChange={handleChange} required={user?.role !== 'admin'}>
                                                {fuelTypes.map(f => <option key={f} value={f}>{f}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Engine Capacity (cc)</label>
                                            <input type="number" name="engineCapacity" value={formData.engineCapacity} onChange={handleChange} placeholder="e.g. 1500" />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Mileage (km)</label>
                                        <input type="number" name="mileage" value={formData.mileage} onChange={handleChange} placeholder="e.g. 50000" required={user?.role !== 'admin'} />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>District</label>
                                            <select name="district" value={formData.district} onChange={handleChange} required={user?.role !== 'admin'}>
                                                <option value="">Select District</option>
                                                {Object.keys(sriLankaLocations).sort().map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>City</label>
                                            <select name="city" value={formData.city} onChange={handleChange} required={user?.role !== 'admin'} disabled={!formData.district}>
                                                <option value="">Select City</option>
                                                {currentCities.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="form-group contact-phone-section" style={{ background: 'rgba(59, 130, 246, 0.05)', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(59, 130, 246, 0.1)', marginBottom: '1.5rem' }}>
                                <label style={{ fontWeight: 600, color: 'var(--color-accent)', marginBottom: '1rem', display: 'block' }}>
                                    <i className="fa-solid fa-phone-volume"></i> Contact Number for this Ad
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="useAccountPhone"
                                            checked={formData.useAccountPhone}
                                            onChange={() => setFormData(prev => ({ ...prev, useAccountPhone: true }))}
                                        />
                                        <span>Use my account number ({user?.phone || 'N/A'})</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="useAccountPhone"
                                            checked={!formData.useAccountPhone}
                                            onChange={() => setFormData(prev => ({ ...prev, useAccountPhone: false }))}
                                        />
                                        <span>Use a different number</span>
                                    </label>

                                    {!formData.useAccountPhone && (
                                        <div style={{ marginTop: '0.5rem' }}>
                                            <input
                                                type="text"
                                                name="customPhone"
                                                value={formData.customPhone}
                                                onChange={handleChange}
                                                placeholder="Enter contact number (e.g. 077 123 4567)"
                                                required={!formData.useAccountPhone && user?.role !== 'admin'}
                                                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Price (Rs.) {selectedCategory === 'Rent' ? 'per Month/Day' : ''}</label>
                                <input type="text" name="price" value={formData.price} onChange={handleChange} placeholder="e.g. 6,500,000" required={user?.role !== 'admin'} />
                            </div>

                            <div className="form-group">
                                <label>Description (Additional Info)</label>
                                <textarea name="description" value={formData.description} onChange={handleChange} rows="4"></textarea>
                            </div>

                            {/* Options Checkboxes */}
                            {["Car", "Van", "SUV", "Jeep", "Truck", "Bus", "Lorry"].includes(formData.vehicleType) && (
                                <div className="form-group">
                                    <label>Options</label>
                                    <div className="checkbox-group">
                                        <label><input type="checkbox" name="airCondition" checked={formData.options.airCondition} onChange={handleChange} /> Air Condition</label>
                                        <label><input type="checkbox" name="powerSteering" checked={formData.options.powerSteering} onChange={handleChange} /> Power Steering</label>
                                        <label><input type="checkbox" name="powerMirror" checked={formData.options.powerMirror} onChange={handleChange} /> Power Mirror</label>
                                        <label><input type="checkbox" name="powerWindow" checked={formData.options.powerWindow} onChange={handleChange} /> Power Window</label>
                                    </div>
                                </div>
                            )}

                            {/* Image Upload - Common for both */}
                            <div className="form-group">
                                <label>Upload Images (Max 6)</label>
                                <input type="file" multiple accept="image/*" onChange={handleImageChange} />
                                <div className="image-preview-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                                    {images.map((img, index) => (
                                        <div
                                            key={index}
                                            className="image-preview"
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => handleDrop(e, index)}
                                            style={{ position: 'relative', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--color-card-bg)', cursor: 'move' }}
                                        >
                                            {index === 0 && (
                                                <div style={{ position: 'absolute', top: '-10px', background: 'var(--color-accent)', color: 'white', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
                                                    Preview Cover
                                                </div>
                                            )}
                                            <div style={{ height: '80px', width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                                                <img src={URL.createObjectURL(img)} alt={`Upload ${index}`} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} draggable="false" />
                                            </div>
                                            <span style={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'center' }}>
                                                {img.name}
                                            </span>
                                            <button type="button" onClick={() => removeImage(index)} className="btn-remove" style={{ color: 'red', marginTop: '0.5rem', width: '100%', padding: '0.2rem', cursor: 'pointer' }}><i className="fa-solid fa-trash"></i> Remove</button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="form-group terms-checkbox-group">
                                <label className="terms-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={agreedToTerms}
                                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                                    />
                                    <span>
                                        I confirm that the ad information is accurate and I agree to the{' '}
                                        <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link>
                                        {' '}and{' '}
                                        <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                                    </span>
                                </label>
                            </div>

                            <button type="submit" className="btn-submit" disabled={loading || !agreedToTerms}>
                                {loading ? 'Posting...' : 'Post Ad Now'}
                            </button>
                        </form>
                    )}
                </>
            )}

            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => navigate('/')}
                title="Success!"
                message="Your ad has been verified and posted successfully!"
            />
        </div>
    );
};

export default PostAd;
